<template>
    <div>
        <card>
            <card-body>
                <h5 class="form-title mb-4">
                    {{ form.id ? 'Edit' : 'Create' }} Workflow
                </h5>

                <div class="alert alert-danger" role="alert" v-if="hasSteps">
                    You need to create one or more step(s).
                </div>

                <div class="row">
                    <div class="col-6">
                        <div class="form-group">
                            <label>Campaign Name</label>
                            <input type="text" class="form-control"
                                v-model="form.name"
                                :class="{'is-invalid': nameAreInvalid}" />

                            <span class="invalid-feedback" v-if="nameAreInvalid">
                                {{ form.errors.get('name') }}
                            </span>
                        </div>

                        <div class="form-group">
                            <label>Select locations associated with this campaign</label>
                            <multiselect
                                :options="allLocations"
                                :multiple="true"
                                track-by="id"
                                label="name"
                                :close-on-select="false"
                                :class="{'is-invalid': locationsAreInvalid}"
                                v-model="form.selectedLocations"
                                placeholder="Select one or more locations"
                                @input="checkIfIsSelectAll"
                            />
                            <span class="invalid-feedback" v-if="locationsAreInvalid">
                                {{ form.errors.get('selectedLocations') }}
                            </span>
                        </div>
                    </div>

                    <div class="col-6">
                        <upload-image
                            image-label="Campaign image"
                            v-model="form.image"
                            :url="campaign.image_url"
                        />
                    </div>
                </div>

                <workflow-campaign-form-template
                    v-if="!type"
                    :selectedLocations="selectedLocations"
                    :errors="form.errors"
                    :steps="form.steps"
                    :variables="variables"
                    :campaigns="campaigns"
                    @preview="preview"
                    @addStep="addStep"
                    @remove="removeStep"
                />

                <div class="d-flex justify-content-center">
                    <button class="btn btn-primary button__steps" @click="addStep('email')">
                        <i class="fa fa-plus mr-1"></i> Email
                    </button>

                    <button class="btn btn-primary mx-2 button__steps" @click="addStep('sms')">
                        <i class="fa fa-plus mr-1"></i> SMS
                    </button>

                    <button class="btn btn-primary button__steps" @click="addStep('campaign')">
                        <i class="fa fa-plus mr-1"></i> Campaign
                    </button>
                </div>
            </card-body>

            <card-footer>
                <div class="float-right">
                    <a class="btn swell-btn btn-secondary" :href="$laroute.route('campaigns.workflow.index')">
                        Cancel
                    </a>
                    <button-loading type="button" class="btn swell-btn btn-primary ml-2"
                        @click="save"
                        :loading="form.submitting">
                        Save
                    </button-loading>
                </div>
            </card-footer>
        </card>
    </div>
</template>

<script>
    export default {
        props: {
            campaign : {
                required: true,
                type: Object
            },
            locations: {
                required: true,
                type: Array
            },
            variables: {
                required: true,
                type: Array
            }
        },

        data() {
            return {
                messages         : {
                    select: 'Please, select one or more location(s)',
                    notCampaignLocation: 'No campaigns match the selected location(s)'
                },
                selectedLocations: {
                    error: true,
                    message: ''
                },
                type             : false,
                form             : new Form(this.setupForm()),
                allLocations     : this.locations,
                campaigns        : [],
                hasSteps         : false
            }
        },

        created() {
            this.filterCampaigns();
            this.setAllToLocations();
            this.selectedLocations.message = this.messages.select
        },

        computed: {
            nameAreInvalid() {
                return this.form.errors.has('name');
            },

            locationsAreInvalid() {
                return this.form.errors.has('selectedLocations');
            }
        },

        watch: {
            'form.selectedLocations': function () {
                this.campaigns = [];

                if (this.form.selectedLocations.length === 0) {
                    this.form.steps.filter((step) => {
                        if (step.send_type === 'campaign') {
                            step.workflow_campaign = null;
                            this.selectedLocations.error = true;
                        }
                    });

                    return;
                }

                this.filterCampaigns();
            },

            campaigns() {
                if (this.form.selectedLocations.length && this.campaigns.length === 0) {
                    this.selectedLocations.message = this.messages.notCampaignLocation;
                    return;
                }

                if (this.form.selectedLocations.length === 0 && this.campaigns.length === 0) {
                    this.selectedLocations.message = this.messages.select;
                    return;
                }

                if (this.form.selectedLocations.length) {
                    this.selectedLocations.error = false;
                }

                if (this.form.selectedLocations.length > 1) {
                    this.campaigns.map((selectedCampaign, index) => {
                        this.form.selectedLocations.filter((selectedLocation) => {
                            if (!selectedLocation.campaigns.length) {
                                this.campaigns = [];
                                this.selectedLocations.error = true;
                                this.selectedLocations.message = this.messages.notCampaignLocation;
                                return;
                            }

                            if (selectedLocation.campaigns.indexOf(selectedCampaign)) return;
                            this.$delete(this.campaigns, index);
                        });
                    });
                }
            }
        },

        methods: {
            filterCampaigns() {
                this.form.selectedLocations.map((selectedLocation) => {
                    selectedLocation.campaigns.filter((campaign) => {
                        if (!this.campaigns.some(elem => elem.id === campaign.id)) {
                            this.campaigns.push(campaign);
                        }
                    });
                });
            },

            setAllToLocations() {
                this.allLocations.unshift({id: 0, name: 'Select All'});
            },

            checkIfIsSelectAll(options) {
                if ( options.filter(f => f.id === 0).length > 0 ) {
                    this.form.selectedLocations = this.allLocations.filter(f => f.id !== 0);
                }
            },

            setupForm() {
                return {
                    id               : this.campaign.id || null,
                    name             : this.campaign.name || null,
                    selectedLocations: this.campaign.locations || [],
                    image            : this.campaign.image || "",
                    removeSteps      : [],
                    steps            : this.campaign.messages || []
                };
            },

            preview(url, index) {
                this.form.steps[index].image_url = url;
            },

            addStep(type) {
                if (type === 'sms') {
                    this.form.steps.push({
                        image     : null,
                        delay_days: '',
                        send_type : 'sms',
                        content   : ''
                    });
                } else if (type === 'email') {
                    this.form.steps.push({
                        image       : null,
                        delay_days  : '',
                        send_type   : 'email',
                        from_address: '',
                        from_name   : '',
                        subject     : '',
                        content     : ''
                    });
                } else if (type === 'campaign') {
                    this.form.steps.push({
                        image            : null,
                        delay_days       : '',
                        send_type        : 'campaign',
                        content          : '',
                        workflow_campaign: null
                    });
                }
            },

            removeStep(index, step) {
                this.$delete(this.form.steps, index);

                if (step.id > 0) {
                    this.form.removeSteps.push(step);
                }
            },

            save() {
                if (!this.form.steps.length) {
                    this.hasSteps = true;
                    return;
                }
                this.hasSteps = false;

                const request = this.campaign.id ? this.update() : this.create();

                request.then(() => {
                    window.location.replace(this.$laroute.route('campaigns.workflow.index'));
                    return false;
                }).catch((response) => this.scrollToFirstError());
            },

            create() {
                return this.form.post(this.$laroute.route('campaigns.workflow.store'));
            },

            update() {
                return this.form.put(this.$laroute.route('campaigns.workflow.update', {workflow: this.form.id}));
            }
        }
    }
</script>

<style lang="scss" scoped>
    .step__item {
        background-color: #f1f1f1;
    }

    .review-wrapper {
        max-width: 100%;
    }

    .img-fluid {
        max-height: 200px;
        text-align: center;
        display: block;
        margin: 0 auto;
    }

    .button__steps {
        min-width: 120px;
    }
</style>
